/**
 * ModalManager - Singleton modal system
 * Only one modal can be open at a time, displays different content
 * Factory function following the app's architectural patterns
 */

import {
  createModal,
  type ModalAPI,
  type ModalAnchor,
} from "@UI/Modal/index.js";

export interface ModalShowConfig {
  readonly content: HTMLElement;
  readonly anchor?: ModalAnchor;
  readonly anchorElement?: HTMLElement;
  readonly offset?: { x: number; y: number };
  readonly width?: number | string;
  readonly onClose?: () => void;
  readonly closeOnBackdrop?: boolean;
  readonly showBackdrop?: boolean;
}

export interface ModalManagerAPI {
  readonly show: (config: ModalShowConfig) => void;
  readonly hide: () => void;
  readonly isVisible: () => boolean;
  readonly getCurrentContent: () => HTMLElement | null;
  readonly destroy: () => void;
}

/**
 * Creates a singleton ModalManager
 * @returns ModalManager API
 */
export const createModalManager = (): ModalManagerAPI => {
  let modalAPI: ModalAPI | null = null;
  let currentContent: HTMLElement | null = null;
  let currentOnClose: (() => void) | null = null;
  let isDestroyed = false;

  /**
   * Clean up current content
   */
  const clearCurrentContent = (): void => {
    if (currentContent && modalAPI) {
      try {
        modalAPI.removeChild(currentContent);
      } catch (error) {
        // Content might already be removed, ignore
      }
    }
    currentContent = null;
    currentOnClose = null;
  };

  /**
   * Handle modal close
   */
  const handleModalClose = (): void => {
    const onClose = currentOnClose;
    clearCurrentContent();

    // Call the content's close handler
    onClose?.();
  };

  /**
   * Show modal with content
   */
  const show = (config: ModalShowConfig): void => {
    if (isDestroyed) return;

    const {
      content,
      anchor = "center",
      anchorElement,
      offset = { x: 0, y: 0 },
      width,
      onClose,
      closeOnBackdrop = true,
      showBackdrop = true,
    } = config;

    // If modal is already open, close current content first
    if (modalAPI && modalAPI.isVisible()) {
      clearCurrentContent();
      modalAPI.hide();
    }

    // Create new modal if needed
    if (!modalAPI) {
      modalAPI = createModal({
        showBackdrop,
        closeOnBackdrop,
        onClose: handleModalClose,
      });

      // Append to document body
      document.body.appendChild(modalAPI.getElement());
    }

    // Update modal configuration for this show
    // Note: We'll need to create a new modal instance for different anchoring
    // For now, destroy and recreate if anchor config changes
    modalAPI.destroy();
    modalAPI = createModal({
      showBackdrop,
      closeOnBackdrop,
      anchor,
      ...(anchorElement && { anchorElement }),
      offset,
      ...(typeof width !== "undefined" ? { width } : {}),
      onClose: handleModalClose,
    });

    // Append to document body
    document.body.appendChild(modalAPI.getElement());

    // Set current content and handlers
    currentContent = content;
    currentOnClose = onClose ?? null;

    // Add content to modal and show
    modalAPI.appendChild(content);
    modalAPI.show();
  };

  /**
   * Hide modal
   */
  const hide = (): void => {
    if (isDestroyed || !modalAPI || !modalAPI.isVisible()) return;

    const onClose = currentOnClose;
    clearCurrentContent();
    modalAPI.hide();

    // Call the content's close handler
    onClose?.();
  };

  /**
   * Check if modal is visible
   */
  const isVisible = (): boolean => {
    return modalAPI?.isVisible() ?? false;
  };

  /**
   * Get current content
   */
  const getCurrentContent = (): HTMLElement | null => {
    return currentContent;
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;
    clearCurrentContent();
    modalAPI?.destroy();
    modalAPI = null;
  };

  return {
    show,
    hide,
    isVisible,
    getCurrentContent,
    destroy,
  };
};

// Singleton instance
let globalModalManager: ModalManagerAPI | null = null;

/**
 * Get the global modal manager instance
 * Creates it if it doesn't exist
 */
export const getModalManager = (): ModalManagerAPI => {
  if (!globalModalManager) {
    globalModalManager = createModalManager();
  }
  return globalModalManager;
};

/**
 * Destroy the global modal manager
 * Useful for cleanup in tests
 */
export const destroyModalManager = (): void => {
  if (globalModalManager) {
    globalModalManager.destroy();
    globalModalManager = null;
  }
};
